import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Accordion — Collapsible content container component for the design system.
 *
 * @param {object} props
 * @param {string|React.ReactNode} props.title - Accordion header title
 * @param {string|React.ReactNode} [props.subtitle] - Optional subtitle/hint text
 * @param {boolean} [props.defaultOpen=false] - Initial expanded state (uncontrolled)
 * @param {boolean} [props.isOpen] - Controlled expanded state
 * @param {(nextState: boolean) => void} [props.onToggle] - Callback on toggle
 * @param {string|React.ReactNode} [props.badge] - Optional badge in trigger header
 * @param {React.ComponentType<{size?: number, className?: string}>} [props.icon] - Optional header icon
 * @param {string} [props.className=''] - Additional CSS classes
 * @param {React.ReactNode} props.children - Collapsible content
 */
export default function Accordion({
  title,
  subtitle,
  defaultOpen = false,
  isOpen: controlledOpen,
  onToggle,
  badge,
  icon: Icon,
  className = '',
  children,
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;

  const handleToggle = () => {
    const next = !open;
    if (!isControlled) {
      setInternalOpen(next);
    }
    if (onToggle) {
      onToggle(next);
    }
  };

  const rootClasses = [
    'ui-accordion',
    open ? 'ui-accordion--open' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses}>
      <button
        type="button"
        className="ui-accordion__trigger"
        onClick={handleToggle}
        aria-expanded={open}
      >
        <div className="ui-accordion__header">
          {Icon && <Icon className="ui-accordion__icon" size={16} aria-hidden />}
          <span className="ui-accordion__title">{title}</span>
          {badge && <span className="ui-accordion__badge">{badge}</span>}
          {subtitle && <span className="ui-accordion__subtitle">{subtitle}</span>}
        </div>
        <ChevronDown className="ui-accordion__chevron" size={16} aria-hidden />
      </button>
      <div className="ui-accordion__content" hidden={!open}>
        <div className="ui-accordion__body">{children}</div>
      </div>
    </div>
  );
}

